import React, { useState, useEffect } from 'react';
import { URLS } from './URLS';

// === HELPER: Определяне на възможните оператори според типа ===
const getOperators = (fieldType) => {
    if (fieldType.includes('Date') || fieldType.includes('Time')) {
        return [
            { label: 'After / From (>=)', op: 'gte', input: 'date' },
            { label: 'Before / To (<=)', op: 'lte', input: 'date' },
            { label: 'Exact Date', op: 'exact', input: 'date' } // Внимавай с часовите зони тук
        ];
    } else if (fieldType.includes('Float') || fieldType.includes('Integer') || fieldType.includes('Number')) {
        return [
            { label: 'Equals (=)', op: 'exact', input: 'number' },
            { label: 'Greater than (>)', op: 'gt', input: 'number' },
            { label: 'Less than (<)', op: 'lt', input: 'number' }
        ];
    } else if (fieldType.includes('Boolean')) {
        return [
            { label: 'Is', op: 'exact', input: 'select', options: ['True', 'False'] }
        ];
    } else {
        // Default (String)
        return [
            { label: 'Contains', op: 'icontains', input: 'text' },
            { label: 'Exact Match', op: 'iexact', input: 'text' }
        ];
    }
};

// === COMPONENT: Tree Item (Updated with Filter Button) ===
const FieldTreeItem = ({ field, allFields, onExpand, onToggleDim, selectedDims, onAddToSum, onAddFilter, level = 0 }) => {
    const children = allFields.filter(f => 
        f.id.startsWith(field.id + '__') && 
        f.id.split('__').length === field.id.split('__').length + 1
    );
    const isExpanded = children.length > 0;
    const isNumeric = field.type.includes('Float') || field.type.includes('Integer') || field.is_virtual;

    return (
        <div style={{ marginLeft: `${level * 20}px`, borderLeft: level > 0 ? '1px dashed #ccc' : 'none' }}>
            <div className="field-row" style={{ display: 'flex', alignItems: 'center', padding: '4px 0', fontSize: '13px' }}>
                <div style={{ width: '20px', textAlign: 'center' }}>
                    {field.is_relation && (
                        <button onClick={() => onExpand(field)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#007bff', fontWeight: 'bold' }}>
                            {isExpanded ? '▼' : '▶'}
                        </button>
                    )}
                </div>

                <input 
                    type="checkbox" 
                    checked={selectedDims.includes(field.id)}
                    onChange={() => onToggleDim(field.id)}
                    style={{ marginRight: '6px' }}
                />
                
                <span style={{ color: field.is_relation ? '#222' : '#555', marginRight: 'auto' }}>
                    {field.label.split('->').pop().trim()}
                </span>

                {/* ACTION BUTTONS */}
                <div style={{ display: 'flex', gap: '5px' }}>
                    {/* Filter Button (Y) */}
                    <button 
                        onClick={() => onAddFilter(field)}
                        title="Add as Filter"
                        style={{ border: '1px solid #6c757d', background: '#fff', color: '#6c757d', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', padding: '1px 5px' }}
                    >
                        Filter
                    </button>

                    {/* Sum Button (∑) */}
                    {isNumeric && (
                        <button 
                            onClick={() => onAddToSum(field)}
                            title="Add to Aggregation"
                            style={{ border: '1px solid #28a745', background: '#e8f5e9', color: '#28a745', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', padding: '1px 5px' }}
                        >
                            +∑
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && children.map(child => (
                <FieldTreeItem 
                    key={child.id} 
                    field={child} 
                    allFields={allFields} 
                    onExpand={onExpand}
                    onToggleDim={onToggleDim}
                    selectedDims={selectedDims}
                    onAddToSum={onAddToSum}
                    onAddFilter={onAddFilter}
                    level={level + 1}
                />
            ))}
        </div>
    );
};

// === MAIN COMPONENT ===
const DynamicReportEngine = ({ urls }) => {
    // --- STATE ---
    const [baseEntity, setBaseEntity] = useState("Project");
    const [availableFields, setAvailableFields] = useState([]); 
    const [loadingMetadata, setLoadingMetadata] = useState(false);
    
    // Configuration
    const [selectedDimensions, setSelectedDimensions] = useState([]); 
    const [activeFilters, setActiveFilters] = useState([]); // [{ id, fieldId, label, type, operator, value }]
    const [customSums, setCustomSums] = useState([]); 
    const [sumBuilder, setSumBuilder] = useState([]); 
    const [pivotField, setPivotField] = useState("");

    // Results
    const [results, setResults] = useState([]);
    const [loadingReport, setLoadingReport] = useState(false);
    const [error, setError] = useState(null);



    useEffect(() => {
        loadMetadataForModel(baseEntity, true);
    }, [baseEntity]);

    const loadMetadataForModel = (modelName, isReset = false) => {
        setLoadingMetadata(true);
        fetch(`${URLS.aj_get_report_metadata}?model=${modelName}`)
            .then(res => res.json())
            .then(result => {
                const newFields = result.fields || [];
                if (isReset) {
                    setAvailableFields(newFields);
                    setSelectedDimensions([]);
                    setActiveFilters([]); // Reset filters too
                    setCustomSums([]);
                    setSumBuilder([]);
                    setResults([]);
                    setPivotField("");
                }
            })
            .catch(err => setError(err.message))
            .finally(() => setLoadingMetadata(false));
    };

    const handleExpand = (field) => {
        if (availableFields.some(f => f.id.startsWith(field.id + '__') && f.id !== field.id)) return;
        if (!field.related_model) return;

        setLoadingMetadata(true);
        fetch(`${URLS.aj_get_report_metadata}?model=${field.related_model}`)
            .then(res => res.json())
            .then(result => {
                const prefixedFields = (result.fields || []).map(child => ({
                    ...child,
                    id: `${field.id}__${child.id}`,
                    label: child.label, 
                    related_model: child.related_model
                }));
                setAvailableFields(prev => [...prev, ...prefixedFields]);
            })
            .finally(() => setLoadingMetadata(false));
    };

    // --- FILTER LOGIC ---
    const addFilter = (field) => {
        const ops = getOperators(field.type);
        setActiveFilters(prev => [
            ...prev,
            {
                uid: Date.now(), // unique ID for React keys
                fieldId: field.id,
                label: field.label,
                type: field.type,
                operator: ops[0].op, // Default operator
                value: '',
                availableOps: ops
            }
        ]);
    };

    const updateFilter = (uid, key, val) => {
        setActiveFilters(prev => prev.map(f => f.uid === uid ? { ...f, [key]: val } : f));
    };

    const removeFilter = (uid) => {
        setActiveFilters(prev => prev.filter(f => f.uid !== uid));
    };

    // --- AGGREGATION & SELECTION ---
    const toggleDimension = (id) => {
        setSelectedDimensions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const addToSumBuilder = (field) => {
        if (!sumBuilder.find(f => f.id === field.id)) setSumBuilder(prev => [...prev, field]);
    };

    const commitSumColumn = () => {
        const name = prompt("Column Name:", "Total");
        if (name) {
            setCustomSums(prev => [...prev, { name, fields: sumBuilder.map(f => f.id) }]);
            setSumBuilder([]);
        }
    };

    // --- REPORT EXECUTION ---
    const processPivot = (data) => {
        if (!pivotField) return data;
        
        // Find the metric key (the calculated/sum column)
        const metricKey = Object.keys(data[0]).find(k => k.includes('__sum') || k.includes('__avg') || customSums.some(cs => cs.name === k));
        if (!metricKey) return data;

        const pivotValues = [...new Set(data.map(item => item[pivotField]))].filter(Boolean);
        const rowKeys = Object.keys(data[0]).filter(k => k !== pivotField && k !== metricKey);
        
        const pivotedData = {};
        data.forEach(item => {
            const rowId = rowKeys.map(k => item[k]).join('|');
            if (!pivotedData[rowId]) {
                pivotedData[rowId] = {};
                rowKeys.forEach(key => pivotedData[rowId][key] = item[key]);
                pivotValues.forEach(val => pivotedData[rowId][val] = 0);
            }
            if (item[pivotField]) pivotedData[rowId][item[pivotField]] = item[metricKey];
        });
        return Object.values(pivotedData);
    };

    const runReport = () => {
        setLoadingReport(true);
        setError(null);

        // Transform UI Filters to Django Lookup format
        // Example: { "startTime__gte": "2023-01-01", "project__name__icontains": "Test" }
        const backendFilters = {};
        activeFilters.forEach(f => {
            if (f.value) {
                const lookupKey = `${f.fieldId}__${f.operator}`;
                backendFilters[lookupKey] = f.value;
            }
        });

        // Ensure pivot field is fetched
        const dimsToSend = [...selectedDimensions];
        if (pivotField && !dimsToSend.includes(pivotField)) dimsToSend.push(pivotField);

        const payload = {
            base_entity: baseEntity,
            dimensions: dimsToSend,
            custom_sums: customSums,
            filters: backendFilters,
            metrics: [] // Using custom_sums mainly
        };

        fetch(URLS.aj_generate_dynamic_report, {
            method: "POST",
            body: JSON.stringify(payload)
        })
        .then(res => res.json())
        .then(data => {
            if (data.error) throw new Error(data.error);
            const final = processPivot(data);
            setResults(final);
        })
        .catch(err => setError(err.message))
        .finally(() => setLoadingReport(false));
    };

    const rootFields = availableFields.filter(f => !f.id.includes('__'));

    return (
        <div style={{ display: 'flex', gap: '20px', fontFamily: 'Segoe UI, sans-serif', padding: '10px' }}>
            
            {/* === SIDEBAR === */}
            <div style={{ width: '400px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                
                {/* 1. Model Selection */}
                <div className="card" style={cardStyle}>
                    <label style={{ fontWeight: 'bold' }}>Base Model:</label>
                    <select value={baseEntity} onChange={e => setBaseEntity(e.target.value)} style={{ width: '100%' }}>
                        <option value="Project">Project</option>
                        <option value="Task">Task (Hours)</option>
                        <option value="Client">Client</option>
                        <option value="BrightUser">User</option>
                    </select>
                </div>

                {/* 2. FIELD TREE */}
                <div className="card" style={{ ...cardStyle, flex: 1, overflowY: 'auto', minHeight: '250px', maxHeight: '400px' }}>
                    <div style={{ borderBottom: '1px solid #eee', paddingBottom: '5px', fontWeight: 'bold' }}>Fields</div>
                    {rootFields.map(f => (
                        <FieldTreeItem 
                            key={f.id} field={f} allFields={availableFields} 
                            onExpand={handleExpand} onToggleDim={toggleDimension} selectedDims={selectedDimensions} 
                            onAddToSum={addToSumBuilder} onAddFilter={addFilter}
                        />
                    ))}
                </div>

                {/* 3. ACTIVE FILTERS PANEL */}
                <div className="card" style={{ ...cardStyle, background: '#fff8f8', border: '1px solid #ffdcdc' }}>
                    <label style={{ fontWeight: 'bold', color: '#c00' }}>Filters</label>
                    {activeFilters.length === 0 && <div style={{fontSize: '12px', color: '#999'}}>Select 'Filter' on any field above</div>}
                    
                    {activeFilters.map(filter => (
                        <div key={filter.uid} style={{ marginTop: '8px', padding: '5px', background: 'white', border: '1px solid #eee', borderRadius: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold' }}>
                                <span>{filter.label}</span>
                                <span onClick={() => removeFilter(filter.uid)} style={{ cursor: 'pointer', color: 'red' }}>×</span>
                            </div>
                            <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                                {/* Operator Select */}
                                <select 
                                    value={filter.operator} 
                                    onChange={(e) => updateFilter(filter.uid, 'operator', e.target.value)}
                                    style={{ fontSize: '11px', width: '40%' }}
                                >
                                    {filter.availableOps.map(op => <option key={op.op} value={op.op}>{op.label}</option>)}
                                </select>

                                {/* Value Input */}
                                {filter.availableOps[0].input === 'select' ? (
                                    <select onChange={(e) => updateFilter(filter.uid, 'value', e.target.value)} style={{ flex: 1 }}>
                                        <option value="True">True</option>
                                        <option value="False">False</option>
                                    </select>
                                ) : (
                                    <input 
                                        type={filter.availableOps[0].input} 
                                        value={filter.value} 
                                        onChange={(e) => updateFilter(filter.uid, 'value', e.target.value)}
                                        placeholder="Value..."
                                        style={{ fontSize: '12px', flex: 1, border: '1px solid #ccc', padding: '2px' }}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* 4. AGGREGATION & PIVOT */}
                <div className="card" style={{ ...cardStyle, background: '#f0f8ff' }}>
                    <label style={{ fontWeight: 'bold', color: '#004085' }}>Aggregation & Pivot</label>
                    
                    {/* Sum Builder */}
                    <div style={{ margin: '5px 0' }}>
                        {sumBuilder.map((f, i) => <span key={i} style={tagStyle}>{f.label}</span>)}
                        <button onClick={commitSumColumn} disabled={!sumBuilder.length} style={{ width: '100%', marginTop: '5px' }}>+ Create Sum Column</button>
                    </div>

                     {/* Pivot Select */}
                    <div style={{ marginTop: '10px', borderTop: '1px dashed #ccc', paddingTop: '5px' }}>
                        <label style={{ fontSize: '12px' }}>Pivot Column (Optional):</label>
                        <select value={pivotField} onChange={e => setPivotField(e.target.value)} style={{ width: '100%', fontSize: '12px' }}>
                            <option value="">-- None --</option>
                            {selectedDimensions.map(id => <option key={id} value={id}>{availableFields.find(f=>f.id===id)?.label || id}</option>)}
                        </select>
                    </div>
                </div>

                <button onClick={runReport} style={{ padding: '12px', background: '#28a745', color: 'white', border: 'none', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer' }}>RUN REPORT</button>
            </div>

            {/* === RESULTS === */}
            <div style={{ flex: 1, overflowX: 'auto', border: '1px solid #ddd', padding: '10px' }}>
                {results.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ background: '#343a40', color: 'white' }}>
                                {Object.keys(results[0]).map(k => <th key={k} style={{ padding: '8px', textAlign: 'left' }}>{k.replace(/__/g, ' › ')}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((row, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                                    {Object.values(row).map((v, j) => <td key={j} style={{ padding: '6px' }}>{typeof v === 'number' ? v.toFixed(2) : (v||'-')}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : <div style={{ textAlign: 'center', marginTop: '50px', color: '#aaa' }}>Configure filters and click Run.</div>}
            </div>
        </div>
    );
};

const cardStyle = { background: 'white', padding: '10px', borderRadius: '5px', border: '1px solid #ddd', marginBottom: '0' };
const tagStyle = { display: 'inline-block', background: '#e2e6ea', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', marginRight: '4px' };

export {DynamicReportEngine}